import { serveSandboxAssets } from './sandboxAssets';
import './thread';
import { initEngine, engineBytes } from './engineModule';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { createRoot } from 'react-dom/client';
import { start as startGame, start_sandbox as startSandbox, Game, set_panic_hook, set_tracing_hook, Status } from '../out/client_web';
import './app.css';
import './session-shell.css';
import { sessionStyles as styles } from './sessionStyles';
import { SessionPanel } from './SessionPanel';
import { startEditorBridge } from './editorBridge';
import { LogLevel, recordSandboxLog, startConsoleCapture } from './consoleCapture';
import { relayYakui } from './yakuiRelay';
import { LatencyCapture } from './latencyCapture';
import { GpuTiming } from './gpuTiming';

let latencyCapture: LatencyCapture | null = null;

// Before anything else runs, so a client that fails on the way up says so in
// the editor's log rather than only in a browser console nobody can see.
startConsoleCapture();

declare global {
    function init_sandbox(assets_url: string, contents_url: string): MessagePort;

    var sandbox_iframe: HTMLIFrameElement | null;
}


// The subdomain names the game: voidrunner.aetherlight.io, or
// voidrunner.localhost locally. A bare domain leaves it to nexus's default.
function gameFromHost(hostname: string): string | null {
    const labels = hostname.split('.');
    const last = labels[labels.length - 1];
    if (/^\d+$/.test(last) || labels[0] === 'www' || labels[0] === 'staging') {
        return null;
    }
    return labels.length >= (last === 'localhost' ? 2 : 3) ? labels[0] : null;
}

globalThis.init_sandbox = (assets_url: string, contents_url: string) => {
    let channel = new MessageChannel();

    // if (globalThis.sandbox_iframe != null) {
    //     document.head.removeChild(globalThis.sandbox_iframe);
    //     globalThis.sandbox_iframe = null;
    // }

    let log_channel = new MessageChannel();
    // The sandbox is a document of its own with no way to reach the editor
    // host, so what it says is relayed into a log of its own from here. A panic
    // is the one kind that carries its text in `error` rather than `message`.
    const sandboxLevels: { [type: string]: LogLevel } = {
        panic: "error",
        error: "error",
        warn: "warn",
        info: "info",
        log: "log",
    };
    log_channel.port1.onmessage = (event) => {
        const level = sandboxLevels[event.data?.type];
        if (level === undefined) {
            console.warn("Unknown message type:", event.data);
            return;
        }
        const text = event.data.type == "panic"
            ? "panic: " + event.data.error
            : event.data.message;
        recordSandboxLog(level, text);
    };

    let iframe = document.createElement("iframe");
    globalThis.sandbox_iframe = iframe;
    iframe.width = "0";
    iframe.height = "0";
    iframe.sandbox = "allow-scripts";
    iframe.src = `${window.location.protocol}//${window.location.host}/sandbox.html`;
    document.head.appendChild(iframe);
    window.addEventListener("message", (event) => {
        // This window is spoken to by more than the sandbox — an editor host
        // sends its requests here too — so anything from elsewhere is left to
        // whoever it was meant for rather than complained about.
        if (event.source !== iframe.contentWindow) {
            return;
        }

        if (event.data === "loadEngine") {
            iframe.contentWindow!.postMessage({ type: "engine", bytes: engineBytes() }, "*");
        } else if (event.data == "loadModule") {
            if (iframe.contentWindow == null) {
                console.error("iframe contentWindow is null");
            }
            else {
                const assets = serveSandboxAssets(contents_url);
                iframe.contentWindow?.postMessage({ type: "init", assets_url, contents_url }, "*", [channel.port2, log_channel.port2, assets]);
            }
        } else if (typeof event.data?.type !== 'string' || !event.data.type.startsWith('yakui.')) {
            // The in-game UI's own messages are the relay's.
            console.warn("Unknown message type:", event.data);
        }
    });

    return channel.port1;
}


// Loading state types
type LoadingPhase =
    | { stage: 'requesting'; message: string }
    | { stage: 'downloading'; loaded: number; total: number | null; message: string }
    | { stage: 'initializing'; message: string }
    | { stage: 'ready'; message: string }
    | { stage: 'error'; message: string; error?: any };

const initialLoading: LoadingPhase = { stage: 'requesting', message: 'Requesting game resources…' };

const LoadingScreen = ({ phase }: { phase: LoadingPhase }) => {
    let percent: number | null = null;
    if (phase.stage === 'downloading') {
        if (phase.total && phase.total > 0) {
            percent = Math.min(100, Math.round((phase.loaded / phase.total) * 100));
        }
    } else if (phase.stage === 'initializing') {
        percent = null;
    } else if (phase.stage === 'ready') {
        percent = 100;
    }

    return (
        <div className="session-shell" style={styles.overlay}>
            <SessionPanel>
                <h1 style={styles.title}>Aether</h1>
                <div style={styles.status} role="status">{phase.message}</div>
                {phase.stage === 'downloading' && (
                    <div style={styles.status}>
                        {(phase.loaded / 1e6).toFixed(1)} MB{phase.total ? ` / ${(phase.total / 1e6).toFixed(1)} MB · ${percent}%` : ' downloaded'}
                    </div>
                )}
                {percent !== null && (
                    <div style={styles.progressOuter} role="progressbar" aria-label="Download progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
                        <div style={{ ...styles.progressInner, width: `${percent}%` }} />
                    </div>
                )}
                {phase.stage === 'error' && (
                    <><div style={styles.error} role="alert">{phase.error instanceof Error ? phase.error.message : "Could not start the game."}</div><button onClick={() => window.location.reload()}>Reload</button></>
                )}
            </SessionPanel>
        </div>
    );
};

type ShareIntent = { query: string; label: string };
function incomingLink(): ShareIntent | null {
    const params = new URLSearchParams(window.location.search);
    const links = [...params].filter(([key]) => key === 'join' || key === 'visit');
    if (!links.length) return null;
    if (links.length !== 1) return { query: '', label: 'This link is malformed.' };
    const [key, value] = links[0];
    const valid = key === 'join' ? /^[a-z0-9]{12,32}$/.test(value) : /^[a-zA-Z0-9_-]{1,32}\/[a-zA-Z0-9_-]{1,64}$/.test(value);
    return valid ? { query: `${key}=${encodeURIComponent(value)}`, label: key === 'join' ? 'Joining a friend' : 'Visiting a home' }
        : { query: '', label: 'This link is malformed.' };
}

function clearIncomingLink() {
    const url = new URL(window.location.href);
    url.searchParams.delete('join');
    url.searchParams.delete('visit');
    window.history.replaceState(null, '', url);
}

const App = () => {
    const [quit, setQuit] = useState(false);
    const { discordRequired, canAttemptGuestLogin, authError, isAuthenticated, accessToken, isLoading, isUnauthorized, startDiscordLogin, doGuestLogin } = useAuth();
    const [gameModuleLoaded, setGameModuleLoaded] = useState<boolean>(false);
    const [game, setGame] = useState<Game | null>(null as any);
    const [connectShard, setConnectShard] = useState<string | null>(null);
    const [loading, setLoading] = useState(initialLoading as LoadingPhase);
    const [inWorld, setInWorld] = useState(false);
    const [linkIntent, setLinkIntent] = useState<ShareIntent | null>(incomingLink);
    const [linkState, setLinkState] = useState<'pending' | 'accepted' | 'refused'>('pending');
    const [linkMessage, setLinkMessage] = useState('Checking link…');
    const [linkRetry, setLinkRetry] = useState(0);
    const [linkNote, setLinkNote] = useState<string | null>(null);
    const linkApplied = useRef(false);
    // What the in-game UI is told of the page, read by the frame loop.
    const linkNoteRef = useRef<string | null>(null);
    linkNoteRef.current = linkNote;
    const tokenRef = useRef<string | null>(null);
    tokenRef.current = accessToken ?? null;
    const rendererStartup = useRef<Promise<void>>(Promise.resolve());
    const activeGameRef = useRef<Game | null>(null);
    const stopGameRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        if (!linkIntent || !accessToken) return;
        if (!linkIntent.query) { setLinkMessage(linkIntent.label); setLinkState('refused'); return; }
        let cancelled = false;
        setLinkState('pending');
        fetch(`/api/share/resolve?${linkIntent.query}`, { headers: { Authorization: `Bearer ${accessToken}` } })
            .then(async response => {
                const data = await response.json();
                if (!response.ok) throw new Error(data.message || 'Could not check this link.');
                if (data.message) throw new Error(data.message);
                if (!data.destination) throw new Error('This destination is unavailable.');
                // A link moves nobody: what it resolves to is where the place is
                // and how to get there, kept on screen once play starts.
                if (!cancelled) { setLinkMessage(data.guidance || data.destination); setLinkState('accepted'); setLinkNote(data.guidance || null); }
            })
            .catch(error => { if (!cancelled) { setLinkMessage(String(error.message || error)); setLinkState('refused'); } });
        return () => { cancelled = true; };
    }, [accessToken, linkIntent, linkRetry]);

    // Set shard name from hash any time the hash changes
    useEffect(() => {
        const updateShardFromHash = () => {
            const hash = window.location.hash;
            if (hash.startsWith("#")) {
                setConnectShard(hash.substring(1));
            } else {
                setConnectShard(null);
            }
        };
        updateShardFromHash();
        window.addEventListener("hashchange", updateShardFromHash);
        return () => {
            window.removeEventListener("hashchange", updateShardFromHash);
        };
    }, []);

    const loadGame = useCallback(async () => {
        try {
            setLoading({ stage: 'requesting', message: 'Checking engine cache…' });

            await initEngine(new URL('/client_web_bg.wasm', location.href),
                (loaded, total) => setLoading({ stage: 'downloading', loaded, total, message: 'Downloading engine…' }),
                () => setLoading({ stage: 'initializing', message: 'Initializing engine…' }));
            setGameModuleLoaded(true);
        } catch (err) {
            console.error('Failed to start game:', err);
            setLoading({ stage: 'error', message: 'Failed to start game', error: err });
        }
    }, []);


    useEffect(() => {
        if (!isAuthenticated && !isLoading && canAttemptGuestLogin && !isUnauthorized) {
            doGuestLogin();
        } else {
            console.info("Logged in with access token, proceeding to load game");
        }
    }, [isAuthenticated, isLoading, canAttemptGuestLogin, isUnauthorized]);

    useEffect(() => {
        let cancelled = false;
        const start = async () => {
            if (quit || !gameModuleLoaded || !accessToken || (linkIntent && linkState !== 'accepted')) return;
            const canvas = document.getElementById('canvas') as HTMLCanvasElement | null;
            if (!canvas) {
                setLoading({ stage: 'error', message: 'Canvas not found' });
                return;
            }
            let newGame: Game | null = null;
            try {
                const width = Math.max(1, Math.round(document.body.clientWidth * window.devicePixelRatio));
                const height = Math.max(1, Math.round(document.body.clientHeight * window.devicePixelRatio));
                canvas.width = width;
                canvas.height = height;
                set_panic_hook((error: any) => {
                    console.error("Panic:", error);
                });
                set_tracing_hook((message: any) => {
                    console.log(message);
                });

                const nexus_url = `${window.location.protocol}//${window.location.host}/api/`;
                const query = !linkApplied.current ? linkIntent?.query ?? null : null;
                if (new URLSearchParams(window.location.search).get('latencyCapture') === '1' && !latencyCapture) {
                    latencyCapture = new LatencyCapture();
                    (globalThis as any).__aetherLatencyCapture = latencyCapture;
                }
                newGame = startGame(nexus_url, canvas, accessToken, connectShard, gameFromHost(window.location.hostname), query);
                await newGame.initialize_renderer(new URLSearchParams(window.location.search).get('renderer'));
                if (cancelled) { newGame.free(); return; }
                if (query) linkApplied.current = true;
                try {
                    const saved = localStorage.getItem('aetherlight.settings.v1');
                    if (saved) newGame.load_settings(saved);
                } catch (error) { console.warn('Could not load local settings', error); }
                activeGameRef.current = newGame;
                setGame(newGame);
                // From here the game draws its own loading screen on the canvas.
                setLoading({ stage: 'ready', message: '' });
            } catch (error) {
                newGame?.free();
                if (!cancelled) {
                    console.error('Failed to initialize renderer:', error);
                    setLoading({ stage: 'error', message: 'Failed to initialize renderer', error });
                }
            }
        };
        rendererStartup.current = rendererStartup.current.then(async () => {
            if (!cancelled) await start();
        });
        return () => {
            cancelled = true;
            const active = activeGameRef.current;
            if (active) {
                activeGameRef.current = null;
                setGame(current => current === active ? null : current);
                if (stopGameRef.current) stopGameRef.current();
                else active.free();
            }
        };
    }, [gameModuleLoaded, accessToken, connectShard, linkIntent, linkState, quit]);

    useEffect(() => {
        if (linkIntent && inWorld) clearIncomingLink();
    }, [linkIntent, inWorld]);

    useEffect(() => {
        loadGame();

        // Prevent iOS safari behaviors
        const preventDefault = (e: Event) => {
            e.preventDefault();
        };

        // Prevent zoom, scroll, and other gestures
        document.addEventListener('touchstart', preventDefault, { passive: false });
        document.addEventListener('touchmove', preventDefault, { passive: false });
        document.addEventListener('touchend', preventDefault, { passive: false });
        document.addEventListener('gesturestart', preventDefault, { passive: false });
        document.addEventListener('gesturechange', preventDefault, { passive: false });
        document.addEventListener('gestureend', preventDefault, { passive: false });

        return () => {
            document.removeEventListener('touchstart', preventDefault);
            document.removeEventListener('touchmove', preventDefault);
            document.removeEventListener('touchend', preventDefault);
            document.removeEventListener('gesturestart', preventDefault);
            document.removeEventListener('gesturechange', preventDefault);
            document.removeEventListener('gestureend', preventDefault);
        };
    }, []);

    useEffect(() => {
        if (game) {
            console.log("Game is loaded");

            game?.set_scale_factor(window.devicePixelRatio);

            if (new URLSearchParams(window.location.search).has('singleShard')) game.set_auto_attach_portals(false);
            // Always tick once before rendering
            game?.tick(0.)
            // The in-game UI: the project's React module, or the engine's own,
            // run in the script sandbox. This page only carries native Yakui
            // trees, input events, client state and the UI's calls.
            const scriptedUi = relayYakui(game, {
                pageState: () => ({
                    touch: isTouchDevice(),
                    active: !document.hidden && document.hasFocus(),
                    pointerLocked: document.pointerLockElement != null,
                    linkNote: linkNoteRef.current,
                    viewport: { width: window.innerWidth, height: window.innerHeight },
                    safeArea: safeArea(),
                }),
                pageOps: pageOperations(tokenRef, () => setLinkNote(null)),
            });

            // `?bench` hands the game and each frame's timings to scripts/client-bench.
            const bench = new URLSearchParams(window.location.search).has('bench')
                ? { game, frames: [] as number[] }
                : null;
            // Browser work belongs on Chrome's own clock/timeline. Tracy's
            // native socket cannot faithfully timestamp work done in WASM.
            const profile = new URLSearchParams(window.location.search).has('profile');
            (globalThis as any).__aetherBench = bench;
            if (new URLSearchParams(window.location.search).has('recordPredictions')) game.record_predictions(true);

            var frame_handle: number;
            let resizeObserver: ResizeObserver | undefined;
            let gpuTiming: GpuTiming | undefined;
            let canvasRatio = window.devicePixelRatio;
            const syncCanvasSize = () => {
                const target = document.getElementById('canvas') as HTMLCanvasElement | null;
                if (!target) return;
                const ratio = window.devicePixelRatio || 1;
                const width = Math.max(1, Math.round(document.body.clientWidth * ratio));
                const height = Math.max(1, Math.round(document.body.clientHeight * ratio));
                if (canvasRatio !== ratio) { canvasRatio = ratio; game?.set_scale_factor(ratio); }
                if (target.width !== width || target.height !== height) {
                    target.width = width;
                    target.height = height;
                    game?.resize(width, height);
                }
            };
            let padTime = 0, hadPad = false;
            const tick = (time: number) => {
                if (activeGameRef.current !== game) return;
                const pad = document.hasFocus() ? Array.from(navigator.getGamepads?.() ?? []).find(pad => pad?.mapping === 'standard') : undefined;
                if (pad || hadPad) {
                    // Triggers count past the client's TRIGGER_THRESHOLD, the same as native.
                    const buttons = pad?.buttons.reduce((bits, button, index) => (index === 6 || index === 7 ? button.value > 0.5 : button.pressed) ? bits | (1 << index) : bits, 0) ?? 0;
                    game?.gamepad_input(pad?.axes[0] ?? 0, pad?.axes[1] ?? 0, pad?.axes[2] ?? 0, pad?.axes[3] ?? 0, buttons, padTime ? (time - padTime) / 1000 : 0, !!pad);
                }
                hadPad = !!pad; padTime = time;
                const start = (bench || latencyCapture || profile) ? performance.now() : 0;
                if (canvasRatio !== window.devicePixelRatio) syncCanvasSize();
                if (!gpuTiming && game?.graphics_api() === 'webgl2' && game?.status() === Status.Connected && game.performance_enabled()) {
                    const target = document.getElementById('canvas') as HTMLCanvasElement | null;
                    const gl = target?.getContext('webgl2');
                    if (gl) gpuTiming = new GpuTiming(gl, ms => game.set_gpu_frame_time(ms));
                }
                game?.tick(time / 1000.);
                const ticked = (bench || latencyCapture || profile) ? performance.now() : 0;
                gpuTiming?.begin(game?.performance_enabled() ?? false);
                try { game?.render(time / 1000.); }
                finally { gpuTiming?.end(); }
                const rendered = (bench || latencyCapture || profile) ? performance.now() : 0;
                if (game) {
                    frame_handle = window.requestAnimationFrame(tick);
                }
                if (game && !game.wants_pointer_lock() && document.pointerLockElement) {
                    pageUnlocking = true;
                    document.exitPointerLock();
                }
                const settingsSave = game?.take_settings_save();
                if (settingsSave) {
                    try { localStorage.setItem('aetherlight.settings.v1', settingsSave); }
                    catch (error) { console.warn('Could not save local settings', error); }
                }
                if (game?.quit_requested()) {
                    setQuit(true);
                    setGame(null);
                    return;
                }
                scriptedUi.pump(time / 1000.);
                const uiDone = profile ? performance.now() : 0;
                if (profile) {
                    for (const [name, from, to] of [
                        ['aether.render', ticked, rendered],
                        ['aether.tick', start, ticked],
                        ['aether.ui', rendered, uiDone],
                        ['aether.frame', start, uiDone],
                    ] as const) {
                        performance.measure(name, { start: from, end: to });
                        performance.clearMeasures(name);
                    }
                }
                if (latencyCapture && game) {
                    latencyCapture.frame(game, time, rendered - ticked, ticked - start, performance.now() - rendered);
                }
                if (bench) {
                    bench.frames.push(time, rendered - ticked, ticked - start + performance.now() - rendered);
                }
                // After ticking simulation, update connection/loading status using latest status snapshot
                let status = game?.status();
                updateConnectionStatus(status, game?.shard_name());
            };
            frame_handle = window.requestAnimationFrame(tick);

            // Mouse lock and input
            let canvas = document.getElementById('canvas') as HTMLCanvasElement | null;
            // The browser's own Escape unlocks, and its keydown may follow the
            // unlock; that one Escape must not also open the settings.
            let pageUnlocking = false, userUnlockedAt = -Infinity;
            const on_pointer_lock_change = () => {
                const locked = document.pointerLockElement === canvas;
                if (!locked && !pageUnlocking) userUnlockedAt = performance.now();
                pageUnlocking = false;
                game?.set_cursor_lock(locked);
            };
            // Moves and releases are heard on the window, so a drag that leaves
            // the canvas still ends.
            const on_mouse_move = (event: MouseEvent) => {
                const rect = canvas?.getBoundingClientRect();
                game?.mouse_move(
                    (event.clientX - (rect?.left ?? 0)) * window.devicePixelRatio,
                    (event.clientY - (rect?.top ?? 0)) * window.devicePixelRatio,
                    event.movementX,
                    event.movementY,
                );
            };
            const on_key_down = (event: KeyboardEvent) => {
                if (event.isComposing || event.key === 'Process') return;
                if (event.code === 'Escape' && performance.now() - userUnlockedAt < 300) return;
                game?.set_keyboard_modifiers(event.shiftKey, event.ctrlKey, event.altKey, event.metaKey);
                const typed = event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
                const uiHandled = typed && game?.text_input(event.key);
                if (!uiHandled && !(event.repeat && event.code === 'KeyM')) game?.key_down(event.code);
                event.preventDefault();
            };
            const on_key_up = (event: KeyboardEvent) => {
                game?.set_keyboard_modifiers(event.shiftKey, event.ctrlKey, event.altKey, event.metaKey);
                game?.key_up(event.code);
                event.preventDefault();
            };
            const on_composition_end = (event: CompositionEvent) => { game?.text_input(event.data); };
            const on_blur = () => { game?.release_all_inputs(); };
            const on_mouse_down = (event: MouseEvent) => {
                if (event.button === 0) latencyCapture?.markAction('primary_down');
                // preventDefault below suppresses click-to-focus, which breaks
                // window keydown listeners when the client runs in an iframe
                // (e.g. the VSCode editor panel) — grab keyboard focus explicitly.
                window.focus();
                let button = event.button + 1; // Convert to 1=left, 2=middle, 3=right.

                game?.mouse_down(button);
                // Play locks from a click on the world, the editor flycam from a right drag.
                if (game?.wants_pointer_lock() && !document.pointerLockElement) {
                    canvas?.requestPointerLock({ unadjustedMovement: true })?.catch?.(() => {});
                }
                event.preventDefault();
            };
            // Right is aim; the page's menu would take the press instead.
            const on_context_menu = (event: MouseEvent) => event.preventDefault();
            const on_wheel = (event: WheelEvent) => {
                // wheelDeltaY is legacy but the best tell of a touchpad swipe.
                game?.mouse_scroll(event.deltaX, event.deltaY, (event as WheelEvent & { wheelDeltaY?: number }).wheelDeltaY ?? 0, event.deltaMode, event.ctrlKey);
                event.preventDefault();
            };
            // Every finger goes to the client, which routes it to the touch
            // buttons the UI drew, the stick, the camera, or a tap.
            const on_touch = (event: TouchEvent) => {
                const rect = canvas?.getBoundingClientRect();
                const scale = window.devicePixelRatio;
                for (const touch of Array.from(event.changedTouches)) {
                    const x = (touch.clientX - (rect?.left ?? 0)) * scale;
                    const y = (touch.clientY - (rect?.top ?? 0)) * scale;
                    if (event.type === 'touchstart') game.touch_start(touch.identifier, x, y);
                    else if (event.type === 'touchmove') game.touch_move(touch.identifier, x, y);
                    else game.touch_end(touch.identifier, x, y, event.type === 'touchcancel');
                }
                event.preventDefault();
            };
            const on_mouse_up = (event: MouseEvent) => {
                if (event.button === 0) latencyCapture?.markAction('primary_up');
                let button = event.button + 1; // Convert to 1=left, 2=right, 3=middle
                game?.mouse_up(button);
            };

            // Lets an editor host (the VSCode extension) drive this viewport.
            // Inert until the host enables editor mode.
            let stopEditorBridge: (() => void) | undefined;
            // Read-only: the committed UI tree and its laid-out controls, for
            // headless tests that drive the UI by control name.
            (globalThis as any).__aetherUiProbe = () => game?.ui_probe();

            if (canvas != null) {
                stopEditorBridge = startEditorBridge(game, canvas);

                window.addEventListener("mousemove", on_mouse_move);
                canvas.addEventListener("mousedown", on_mouse_down);
                window.addEventListener("mouseup", on_mouse_up);
                canvas.addEventListener("contextmenu", on_context_menu);
                canvas.addEventListener("wheel", on_wheel, { passive: false });
                for (const name of ['touchstart', 'touchmove', 'touchend', 'touchcancel'] as const) {
                    canvas.addEventListener(name, on_touch, { passive: false });
                }
                window.addEventListener("keydown", on_key_down);
                window.addEventListener("keyup", on_key_up);
                window.addEventListener("compositionend", on_composition_end);
                window.addEventListener("blur", on_blur);

                resizeObserver = new ResizeObserver(syncCanvasSize);
                resizeObserver.observe(document.body);
                syncCanvasSize();
                document.addEventListener("pointerlockchange", on_pointer_lock_change);

            }

            let stopped = false;
            const stop = () => {
                if (stopped) return;
                stopped = true;
                gpuTiming?.clear();
                resizeObserver?.disconnect();
                scriptedUi.stop();
                stopEditorBridge?.();
                window.cancelAnimationFrame(frame_handle);
                window.removeEventListener("keydown", on_key_down);
                window.removeEventListener("keyup", on_key_up);
                window.removeEventListener("compositionend", on_composition_end);
                window.removeEventListener("blur", on_blur);
                window.removeEventListener("mousemove", on_mouse_move);
                window.removeEventListener("mouseup", on_mouse_up);
                document.removeEventListener("pointerlockchange", on_pointer_lock_change);
                if (canvas != null) {
                    canvas.removeEventListener("mousedown", on_mouse_down);
                    canvas.removeEventListener("contextmenu", on_context_menu);
                    canvas.removeEventListener("wheel", on_wheel);
                    for (const name of ['touchstart', 'touchmove', 'touchend', 'touchcancel'] as const) {
                        canvas.removeEventListener(name, on_touch);
                    }
                }
                if (stopGameRef.current === stop) stopGameRef.current = null;
                if (activeGameRef.current === game) activeGameRef.current = null;
                game.free();
            };
            stopGameRef.current = stop;
            return stop;
        }
    }, [game]);

    // Integrate status checks inside the render/tick loop instead of polling separately
    // Connection progress is the game's own loading screen; the page only
    // takes over again when the connection is lost.
    const updateConnectionStatus = useCallback((status: Status, _shard_name: string) => {
        setInWorld(status === Status.Connected);
        if (status === Status.Disconnected) {
            setLoading((prev: LoadingPhase) => prev.stage === 'error' && prev.message === 'Disconnected' ? prev : { stage: 'error', message: 'Disconnected' });
        }
    }, []);

    return (
        <>
            {quit && <div className="session-shell" style={styles.overlay}><SessionPanel><h1 style={styles.title}>You left the game</h1><button onClick={() => setQuit(false)}>Play again</button></SessionPanel></div>}
            <canvas id="canvas" style={{ display: 'block', visibility: game && loading.stage === 'ready' ? 'visible' : 'hidden' }}></canvas>
            {!isLoading && !isAuthenticated && (discordRequired || isUnauthorized) ? (
                <div className="session-shell" style={styles.overlay}><SessionPanel>
                    <h1 style={styles.title}>{authError ? 'Unable to sign in' : 'Sign in to play'}</h1>
                    <p role={authError ? 'alert' : undefined}>{authError || (canAttemptGuestLogin ? 'Checking your guest access…' : 'Use an approved guest link or sign in with Discord.')}</p>
                    {authError && canAttemptGuestLogin && <button onClick={() => void doGuestLogin()}>Retry guest sign-in</button>}
                    <button onClick={() => void startDiscordLogin()}>Continue with Discord</button>
                </SessionPanel></div>
            ) : linkIntent && linkState !== 'accepted' ? (
                <div className="session-shell" style={styles.overlay}>
                    <SessionPanel>
                        <h1 style={styles.title}>{linkIntent.label}</h1>
                        <p role="status">{linkMessage}</p>
                        {linkState === 'refused' && <>
                            <button onClick={() => setLinkRetry(n => n + 1)}>Try link again</button>{' '}
                            <button onClick={() => { clearIncomingLink(); setLinkIntent(null); }}>Play normally</button>
                        </>}
                    </SessionPanel>
                </div>
            ) : loading.stage !== 'ready' && <LoadingScreen phase={loading} />}
        </>
    );
};

function isTouchDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
}

/** The notch and home-bar insets the in-game UI keeps clear of, read once per
 * frame from a hidden probe padded by the CSS environment. */
let safeProbe: HTMLDivElement | null = null;
function safeArea() {
    if (!safeProbe) {
        safeProbe = document.createElement('div');
        safeProbe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;inset:0;'
            + 'padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)';
        document.body.appendChild(safeProbe);
    }
    const style = getComputedStyle(safeProbe);
    const px = (value: string) => Math.round(parseFloat(value) || 0);
    return { top: px(style.paddingTop), right: px(style.paddingRight), bottom: px(style.paddingBottom), left: px(style.paddingLeft) };
}

/** What the in-game UI may ask of this page, which alone holds the session,
 * the clipboard and pointer lock. Each answers or throws why not. */
function pageOperations(token: { current: string | null }, dismissLinkNote: () => void) {
    let lastLink: string | null = null;
    return {
        'share.make': async ({ kind }: { kind?: string }) => {
            if (kind !== 'join' && kind !== 'home') throw new Error('unknown kind of link');
            if (!token.current) throw new Error('Not signed in yet.');
            const response = await fetch(kind === 'join' ? '/api/share' : '/api/share/home', { headers: { Authorization: `Bearer ${token.current}` } });
            if (!response.ok) throw new Error('Could not make a link right now.');
            const data = await response.json();
            const value = kind === 'join' ? data.code : data.home;
            if (!value) throw new Error(kind === 'home' ? 'Claim a home plot before sharing it.' : 'No link is available.');
            const url = new URL(window.location.origin + window.location.pathname);
            url.searchParams.set(kind === 'join' ? 'join' : 'visit', value);
            lastLink = url.href;
            try {
                await navigator.clipboard.writeText(url.href);
                return { url: url.href, message: 'Copied. Your friend can open this link in a browser.' };
            } catch {
                return { url: url.href, message: 'Press Copy link, or copy it from here.' };
            }
        },
        // Only a link this page made: the UI is not handed the clipboard.
        'clipboard.copy': async ({ text }: { text?: string }) => {
            if (!lastLink || text !== lastLink) throw new Error('only a link made here can be copied');
            await navigator.clipboard.writeText(text);
            return 'Copied.';
        },
        'pointer.release': () => { document.exitPointerLock?.(); return true; },
        'link.dismiss': () => { dismissLinkNote(); return true; },
        'editor.scriptedMesh.inspect': () => {
            throw new Error('Local mesh source inspection requires a native client started with --project-root. The model preview remains available here.');
        },
    };
}

document.addEventListener("DOMContentLoaded", function () {
    const rootNode = document.getElementById('root') as HTMLElement;
    const root = createRoot(rootNode);

    root.render(
        <AuthProvider>
            <App />
        </AuthProvider>
    );
});

async function fetch_string(url: string): Promise<string> {
    let response = await fetch(url);
    return await response.text();
}
